import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { trackButtonClick } from '../services/api';
import ContactForm from './ContactForm';

const FinalCTA = () => {
  const { finalCtaContent } = useContent();

  const handleBookCallClick = () => {
    trackButtonClick('final-cta-book-call', 'Book a Free Strategy Call', '/');
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[var(--accent-teal)]/10 via-[var(--bg-page)] to-[var(--accent-purple)]/10" />
      
      {/* Animated Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-[var(--accent-teal)] blur-[100px] opacity-20"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-[var(--accent-gold)] blur-[100px] opacity-20"
      />

      <div className="container-main relative z-10">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          {/* Left: CTA Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center lg:text-left"
          >
            {/* Icon */}
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--accent-gold)] to-[var(--accent-teal)] mb-8"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>

            {/* Headline */}
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-6">
              <span className="gradient-text-gold">Own Your Voice.</span>
              <br />
              <span className="gradient-text-gold">Elevate Your Influence.</span>
            </h2>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-[var(--text-muted)] mb-8 md:mb-10 max-w-xl">
              {finalCtaContent.subheadline}
            </p>

            {/* CTA Button */}
            <a 
              href={finalCtaContent.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBookCallClick}
              className="btn-primary text-base px-8 py-4 animate-pulse-glow inline-flex items-center gap-2"
              data-testid="final-cta-book-call-btn"
            >
              {finalCtaContent.primaryCta}
              <ArrowRight className="w-5 h-5" />
            </a>

            {/* Trust Indicator */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-sm text-[var(--text-muted)] mt-6"
            >
              Free 30-minute strategy session
            </motion.p>
          </motion.div>

          {/* Right: Contact Form */}
          <ContactForm />
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
